import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar, Users, ArrowLeft, Clock } from "lucide-react";
import DOMPurify from "dompurify";
import { getEventById } from "../../utils/api/eventApi.js";
import { getImageUrl } from "../../utils/imageUtils.js";
import { useLanguage } from "../../context/LanguageContext.jsx";

const STATUS_CONFIG = {
  ne: {
    predefined: { label: "लाइभ", bg: "#dcfce7", text: "#166534", dot: "#16a34a" },
    upcoming:   { label: "आगामी", bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
    past:       { label: "सम्पन्न", bg: "#f1f5f9", text: "#475569", dot: "#94a3b8" },
  },
  en: {
    predefined: { label: "Live", bg: "#dcfce7", text: "#166534", dot: "#16a34a" },
    upcoming:   { label: "Upcoming", bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
    past:       { label: "Completed", bg: "#f1f5f9", text: "#475569", dot: "#94a3b8" },
  },
};

const CONTENT = {
  ne: {
    loading: "कार्यक्रम लोड हुँदैछ...",
    error: "कार्यक्रम लोड गर्न असफल भयो",
    notFound: "कार्यक्रम भेटिएन",
    backToEvents: "कार्यक्रमहरूमा फर्कनुहोस्",
    aboutHeading: "यो कार्यक्रमको बारेमा",
    galleryHeading: "कार्यक्रमका तस्बिरहरू",
    noGallery: "अहिलेसम्म कुनै तस्बिर छैन",
  },
  en: {
    loading: "Loading event...",
    error: "Failed to load event",
    notFound: "Event not found",
    backToEvents: "Back to Events",
    aboutHeading: "About This Event",
    galleryHeading: "Event Gallery",
    noGallery: "No gallery images yet",
  },
};

const dateLocale = (lang) => (lang === "ne" ? "en-NP" : "en-US");

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = CONTENT[language];
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        const data = await getEventById(id);
        // data from your backend returns { event: {...} }

        setEvent(data.event || data);
      } catch (err) {
        setError(t.error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  if (loading) return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#64748b" }}>{t.loading}</p>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#ef4444" }}>{error}</p>
    </div>
  );

  if (!event) return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p>{t.notFound}</p>
    </div>
  );

  const cfg = STATUS_CONFIG[language][event.status] || STATUS_CONFIG[language].upcoming;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 20px 60px" }}>

      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", color: "rgb(20, 83, 45)", fontWeight: 600, fontSize: 14, marginBottom: 24, marginTop: 60, position: "relative", zIndex: 1}}>
        <ArrowLeft size={16} /> {t.backToEvents}
      </button>

      {/* Hero image  */}
      <div style={{ borderRadius: 16, overflow: "hidden", marginBottom: 28, height: 280, position: "relative" }}>
        {event.imageUrl ? (
          <img src={getImageUrl(event.imageUrl)} alt={event.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg, rgb(20,83,45), rgb(21,128,61))", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Calendar size={60} color="rgba(255,255,255,0.3)" />
          </div>
        )}
        {/* Status badge overlay */}
        <div style={{ position: "absolute", top: 16, left: 16 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: cfg.bg, color: cfg.text }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.dot }} />
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Title */}
      <h1 style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", margin: "0 0 16px", lineHeight: 1.2 }}>
        {event.title}
      </h1>

      {/* Meta info */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 24px", marginBottom: 24 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#475569" }}>
          <Calendar size={15} color="rgb(20,83,45)" />
          {new Date(event.date).toLocaleDateString(dateLocale(language), { day: "numeric", month: "long", year: "numeric" })}
        </span>
        {event.time && (
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#475569" }}>
            <Clock size={15} color="rgb(20,83,45)" />
            {event.time}
          </span>
        )}
        {event.organizer && (
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#475569" }}>
            <Users size={15} color="rgb(20,83,45)" />
            {event.organizer}
          </span>
        )}

      </div>

      {/* Divider */}
      <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", marginBottom: 24 }} />

      {/* Description */}
      <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>{t.aboutHeading}</h2>
      <div
        className="event-description-content"
        style={{ fontSize: 15, color: "#475569", lineHeight: 1.8 }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(event.description || "") }}
      />
      <style>{`
  .event-description-content p { margin: 0 0 14px; }
  .event-description-content p:last-child { margin-bottom: 0; }
  .event-description-content ul, .event-description-content ol { margin: 0 0 14px; padding-left: 22px; }
  .event-description-content ul { list-style-type: disc; }
  .event-description-content ol { list-style-type: decimal; }
  .event-description-content li { margin-bottom: 6px; display: list-item; }
  .event-description-content strong { color: #0f172a; }
  .event-description-content a { color: rgb(20,83,45); text-decoration: underline; }
  .event-description-content h1, .event-description-content h2, .event-description-content h3 {
    color: #0f172a; font-weight: 700; margin: 20px 0 10px;
  }
  .event-description-content blockquote {
    margin: 0 0 14px; padding-left: 14px; border-left: 3px solid #e2e8f0; color: #64748b;
  }
`}</style>
      {/* Gallery */}
      <div style={{ marginTop: 40 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>
          {t.galleryHeading}
        </h2>
        {event.gallery && event.gallery.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {event.gallery.map((url, i) => (
              <div key={i} style={{ borderRadius: 12, overflow: "hidden", height: 160 }}>
                <img src={getImageUrl(url)} alt={`Gallery ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: "40px 20px", textAlign: "center", background: "#f8fafc", borderRadius: 12, border: "1px dashed #e2e8f0" }}>
            <p style={{ color: "#94a3b8", fontSize: 14 }}>{t.noGallery}</p>
          </div>
        )}
      </div>

    </div>
  );
};